<template>
  <div class="access-station">

      <div class="state state-connecting" key="connecting" v-if="state === 'connecting'">
        <grid-loader :done="httpSuccess" @complete="loading = false"/>
        <h3>{{ loadingLabel }}</h3>
      </div>

      <div class="state" :class="['state-' + state]" :key="state" v-else>

        <p class="station-name" v-if="station && station.name">{{ station.name }}</p>

        <div class="stepper">
          <span
            v-for="step in steps"
            :key="step.key"
            class="step"
            :class="'step-' + step.status"
            :title="step.label"
          />
        </div>

        <div class="hero" v-if="state === 'standby'">
          <fa icon="power-off" class="hero-icon hero-icon-gray" />
        </div>
        <div class="hero" v-else-if="state === 'loggingIn'">
          <fa icon="user-lock" class="hero-icon hero-icon-pulse" />
        </div>
        <div class="hero" v-else-if="state === 'readyToFill'">
          <fa icon="play-circle" class="hero-icon hero-icon-accent" />
        </div>
        <div class="hero" v-else-if="state === 'filling'">
          <fa icon="circle" class="hero-icon hero-icon-pulse hero-icon-accent" />
        </div>
        <div class="hero" v-else-if="state === 'completed'">
          <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
            <circle class="checkmark__circle" cx="26" cy="26" r="25" fill="none"/>
            <path class="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
          </svg>
        </div>
        <div class="hero" v-else-if="state === 'error'">
          <fa icon="exclamation-triangle" class="hero-icon hero-icon-danger" />
        </div>

        <h2 class="primary-label">{{ primaryLabel }}</h2>
        <p class="secondary-label">{{ secondaryLabel }}</p>

        <div class="actions">
          <template v-if="state === 'completed'">
            <el-button type="primary" class="action" @click="disconnect">Done</el-button>
          </template>
          <template v-else-if="state === 'error'">
            <el-button class="action" @click="retry">Retry</el-button>
            <el-button type="danger" class="action" @click="disconnect">Disconnect</el-button>
          </template>
          <template v-else>
            <el-button :type="state === 'filling' ? 'warning' : 'danger'" class="action" @click="disconnect">
              Disconnect
            </el-button>
          </template>
        </div>

      </div>

  </div>
</template>

<script>
import GridLoader from '@/components/shared/loaders/GridLoader'

const TASK_STANDBY = 0
const TASK_LOGGING_IN = 1
const TASK_READY_TO_FILL = 2
const TASK_FILLING = 3
const TASK_COMPLETED = 4
const TASK_ERROR = 5

const STATE_BY_TASK = {
  [TASK_STANDBY]: 'standby',
  [TASK_LOGGING_IN]: 'loggingIn',
  [TASK_READY_TO_FILL]: 'readyToFill',
  [TASK_FILLING]: 'filling',
  [TASK_COMPLETED]: 'completed',
  [TASK_ERROR]: 'error'
}

const STEP_DEFS = [
  { key: 'loggingIn', task: TASK_LOGGING_IN, label: 'Logging in' },
  { key: 'readyToFill', task: TASK_READY_TO_FILL, label: 'Ready' },
  { key: 'filling', task: TASK_FILLING, label: 'Filling' },
  { key: 'completed', task: TASK_COMPLETED, label: 'Done' }
]

const PRIMARY_LABELS = {
  standby: 'Standby',
  loggingIn: 'Logging in',
  readyToFill: 'Ready to fill',
  filling: 'Filling',
  completed: 'Fill complete',
  error: 'Station error'
}

const SECONDARY_LABELS = {
  standby: 'Waiting for the station to start',
  loggingIn: 'Authenticating with station',
  readyToFill: 'Start the fill on the station',
  filling: 'Do not disconnect during fill',
  completed: 'You can safely disconnect',
  error: 'The station reported an error'
}

export default {
  name: 'AccessStation',
  components: { GridLoader },
  data () {
    return {
      autoDisconnect: true,
      loadingLabel: 'Connecting to station',
      httpSuccess: false,
      loading: true,
      animated: false,
      currentTask: TASK_READY_TO_FILL,
      lastUpdate: null,
      lastActiveTask: null,
      station: {
        name: 'Dummy station'
      }
    }
  },
  computed: {
    state () {
      if (this.loading) return 'connecting'
      return STATE_BY_TASK[this.currentTask] || 'standby'
    },
    primaryLabel () {
      return PRIMARY_LABELS[this.state] || ''
    },
    secondaryLabel () {
      return SECONDARY_LABELS[this.state] || ''
    },
    steps () {
      return STEP_DEFS.map(def => {
        let status = 'pending'
        if (this.state === 'error') {
          if (def.task === this.lastActiveTask) status = 'error'
          else if (this.lastActiveTask != null && def.task < this.lastActiveTask) status = 'done'
        } else if (this.currentTask === TASK_COMPLETED) {
          status = 'done'
        } else if (this.currentTask >= TASK_LOGGING_IN && this.currentTask <= TASK_FILLING) {
          if (def.task < this.currentTask) status = 'done'
          else if (def.task === this.currentTask) status = 'current'
        }
        return { key: def.key, label: def.label, status }
      })
    }
  },
  methods: {
    disconnect () {
      this.$router.push({ name: 'stations' })
    },
    disconnectStation (cb) {
      this.$http.get(this.$api.station(this.$route.params.sId).disconnect)
        .then(response => {
          cb()
        }, _ => {
          this.$message.error('Could not disconnect')
        })
    },
    mapPayload (raw) {
      if (!raw || typeof raw.currentTask !== 'number') return null
      return {
        task: raw.currentTask,
        connected: raw.connected === true,
        stationId: raw.stationId
      }
    },
    handleConnectUpdate (raw) {
      const mapped = this.mapPayload(raw)
      let task = mapped.task
      if (task < TASK_STANDBY || task > TASK_ERROR) {
        console.warn('connectUpdate: unknown task code, falling back to standby', task)
        task = TASK_STANDBY
      }
      this.lastUpdate = mapped
      if (task === TASK_STANDBY) this.lastActiveTask = null
      else if (task !== TASK_ERROR) this.lastActiveTask = task
      this.currentTask = task
    },
    issueConnect () {
      return this.$http.get(this.$api.station(this.$route.params.sId).connect)
        .then(response => {
          this.loading = false
          this.httpSuccess = true
          this.station = response.data
          setTimeout(() => {
            this.animated = true
          }, 2000)
        }, _ => {
          this.$message.error('Could not connect')
          this.$router.push({ name: 'stations' })
        })
    },
    retry () {
      this.currentTask = TASK_STANDBY
      this.lastActiveTask = null
      this.lastUpdate = null
      this.httpSuccess = false
      this.loading = true
      this.issueConnect()
    }
  },
  created () {
    const signalR = require('@aspnet/signalr')

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.API.replace('/api/', '')}/userHub`, { accessTokenFactory: () => localStorage.getItem('id_token') })
      .build()

    this.connection.on('connectUpdate', data => this.handleConnectUpdate(data))

    this.connection.start()

    this.issueConnect()
  },
  beforeRouteLeave (to, from, next) {
    this.disconnectStation(() => {
    })
    next()
  },
  beforeDestroy () {
    if (this.connection) {
      this.connection.stop()
    }
  }
}
</script>

<style lang="scss" scoped>
$neutral: #606266;
$accent: #008fcc;
$danger: #f56c6c;

.state {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 89vh;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

h3 {
  font-size: 20px;
  font-weight: 600;
  margin-top: 30px;
  color: $neutral;
}

.station-name {
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: lighten($neutral, 15%);
  margin: 0 0 24px 0;
}

.stepper {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 40px;
}

.step {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: lighten($neutral, 45%);
  transition: background 0.3s ease, transform 0.3s ease;

  &.step-done {
    background: $accent;
  }

  &.step-current {
    background: $accent;
    transform: scale(1.4);
    box-shadow: 0 0 0 4px rgba($accent, 0.18);
  }

  &.step-error {
    background: $danger;
    transform: scale(1.4);
    box-shadow: 0 0 0 4px rgba($danger, 0.18);
  }
}

.hero {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 220px;
  height: 220px;
  margin-bottom: 30px;
}

.hero-icon {
  font-size: 140px;
  color: $neutral;
}

.hero-icon-gray {
  color: lightgrey;
}

.hero-icon-accent {
  color: $accent;
}

.hero-icon-danger {
  color: $danger;
}

.hero-icon-pulse {
  animation: pulse 1.6s ease-in-out infinite;
}

.hero-icon-spin {
  animation: spin 1.4s linear infinite;
  color: $accent;
}

@keyframes pulse {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%      { opacity: 1;   transform: scale(1.06); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.hero-bubbles {
  position: relative;
}

.primary-label {
  margin: 0 0 10px 0;
  font-size: 42px;
  color: $neutral;
}

.secondary-label {
  color: $neutral;
  font-size: 16px;
  margin: 0;
  text-align: center;
  max-width: 420px;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 60px;
}

.action {
  min-width: 180px;
}

.bubble {
  animation: expand 1s ease-in-out infinite;
  border-radius: 20px;
  display: inline-block;
  transform-origin: center center;
  margin: 0 10px;
  width: 20px;
  height: 20px;
}

.bubble:nth-child(1) {
  background: $accent;
}

.bubble:nth-child(2) {
  animation-delay: 180ms;
  background: $accent;
}

.bubble:nth-child(3) {
  animation-delay: 360ms;
  background: $accent;
}

.bubble:nth-child(4) {
  animation-delay: 540ms;
  background: $accent;
}

@keyframes expand {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.55);
  }
}

.checkmark {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  display: block;
  stroke-width: 2;
  stroke: $accent;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #F5F9FF;
  animation: fill .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both;
}

.checkmark__circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 2;
  stroke-miterlimit: 10;
  stroke: $accent;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark__check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: $accent;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}
@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}
@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 200px #F5F9FF;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/stations/AccessStation.vue